import * as React from 'react'

import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import UserSelector from 'modules/core/components/UserSelector'
import { Fields } from 'modules/core/components/UserSelector/_filters/filterFields'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import usersWithRegularAccess from 'modules/core/lib/usersWithRegularAccess'
import users, { employeeUser } from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import hasRegularAccess from 'modules/employees/lib/hasRegularAccess'
import { peopleManagedByEmployee } from 'modules/employees/store/collections/employeesContractVersions'
import { getPermissionVerbs } from 'modules/surveys/lib/getPermissionVerbs'
import type { FormType } from 'modules/surveys/store/models/Form'
import memberships from 'modules/teams/store/collections/memberships'
import teams from 'modules/teams/store/collections/teams'
import { peopleLedByEmployee } from 'modules/teams/store/peopleLedByEmployee'

interface Props {
  members: number[]
  setMembers: (ids: number[]) => void
  formType: FormType
  everybody?: boolean
  contractVersions: ContractVersions
}

export function computeAvailableUsers(
  formType: FormType,
  userId: number,
  everybody = false,
  contractVersions: ContractVersions
): User[] {
  const verbs = getPermissionVerbs(formType)

  const verbType = verbs.forms?.everybody
  if (everybody || (verbType && users.current.policy.can(verbType))) {
    return usersWithRegularAccess(users.active)
  }

  let list = [users.current]

  const employee = users.current.employee

  if (!employee) return list

  const reporteesVerbType = verbs.forms?.reportees
  if (reporteesVerbType && users.current.policy.can(reporteesVerbType)) {
    list = list.concat(
      peopleManagedByEmployee(employee.id, contractVersions).map((e) => employeeUser(e))
    )
  }

  const teamVerbType = verbs.forms?.team
  if (teamVerbType && users.current.policy.can(teamVerbType)) {
    list = list.concat(
      peopleLedByEmployee(teams, memberships, employee.id, contractVersions).map((e) =>
        employeeUser(e)
      )
    )
  }

  const ownedVerbType = verbs.forms?.owned
  if (ownedVerbType && !users.current.policy.can(ownedVerbType)) {
    list = list.filter((user) => user.id !== userId)
  }

  list = list.filter(
    (user) => hasRegularAccess(!!user.employee?.attributes.has_regular_access) || !user.employee
  )

  return [...new Set(list)]
}

function ManualSelection({
  members,
  setMembers,
  formType,
  everybody = false,
  contractVersions,
}: Props) {
  const _setMembers = (ids: number[]) => setMembers(ids)

  const {
    access: { id: userId },
  } = useCurrentSession()
  const setMember = (id: number | null | undefined) => {
    if (!id) return setMembers([])

    setMembers(xor(members, [id]))
  }

  // This is a bit of an expensive computation, so we are memoizing it at the
  // expense of less reactivity.
  const availableUsers = React.useRef(
    computeAvailableUsers(formType, userId, everybody, contractVersions)
  ).current

  return (
    <UserSelector
      users={availableUsers}
      onSelect={setMember}
      onSelectMultiple={_setMembers}
      selectedIds={members}
      hideFields={[Fields.EmployeeStatus, Fields.UserType]}
      contractVersions={contractVersions}
    />
  )
}

export default observer(ManualSelection)
